/**
 * CalendarTab — upcoming earnings + macro events.
 * Hits /api/calendar?days=14.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { ORG, GRN, RED, MUT, FONT } from '@/theme';

interface EarningsRow {
  date:    string;
  symbol:  string;
  time:    'bmo' | 'amc' | 'tbd';
  eps_est: number | null;
  rev_est: number | null;
}
interface MacroRow {
  date:    string;
  event:   string;
  importance: 1 | 2 | 3;
  forecast: string | null;
  prior:    string | null;
}
interface CalendarPayload { earnings: EarningsRow[]; macro: MacroRow[] }

export function CalendarTab() {
  const { data, isLoading } = useQuery<CalendarPayload>({
    queryKey: ['calendar', 14],
    queryFn:  () => api('/api/calendar?days=14'),
    staleTime: 600_000,
  });

  if (isLoading) return <Cent>LOADING CALENDAR...</Cent>;
  if (!data)     return <Cent>CALENDAR UNAVAILABLE</Cent>;

  // Defensive: tolerate either the {earnings, macro} object or an empty/legacy
  // shape so a provider hiccup never crashes the tab.
  const earnings = Array.isArray(data.earnings) ? data.earnings : [];
  const macro    = Array.isArray(data.macro)    ? data.macro    : [];
  if (earnings.length === 0 && macro.length === 0) {
    return <Cent>NO UPCOMING EVENTS · CONNECT FINNHUB FOR LIVE EARNINGS</Cent>;
  }

  return (
    <div style={{ display: 'flex', gap: 16, fontFamily: FONT, fontSize: 11 }}>
      {/* Earnings */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: ORG, fontSize: 10, letterSpacing: 1, marginBottom: 4 }}>EARNINGS</div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: MUT, fontSize: 9 }}>
              <th style={th}>Date</th>
              <th style={th}>Sym</th>
              <th style={th}>Time</th>
              <th style={th}>EPS Est</th>
              <th style={th}>Rev Est</th>
            </tr>
          </thead>
          <tbody>
            {earnings.map((r, i) => (
              <tr key={i}>
                <td style={td}>{r.date ?? '—'}</td>
                <td style={{ ...td, color: ORG }}>{r.symbol}</td>
                {/* r.time is typed 'bmo'|'amc'|'tbd' but a real feed can send
                    null -- an unguarded .toUpperCase() there crashes the tab. */}
                <td style={td}>{String(r.time ?? '').toUpperCase() || '—'}</td>
                <td style={td}>{Number.isFinite(r.eps_est as number) ? (r.eps_est as number).toFixed(2) : '—'}</td>
                <td style={td}>{Number.isFinite(r.rev_est as number) ? `$${((r.rev_est as number) / 1e9).toFixed(2)}B` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Macro */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: ORG, fontSize: 10, letterSpacing: 1, marginBottom: 4 }}>MACRO</div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: MUT, fontSize: 9 }}>
              <th style={th}>Date</th>
              <th style={{ ...th, textAlign: 'left' }}>Event</th>
              <th style={th}>★</th>
              <th style={th}>Forecast</th>
              <th style={th}>Prior</th>
            </tr>
          </thead>
          <tbody>
            {macro.map((r, i) => (
              <tr key={i}>
                <td style={td}>{r.date}</td>
                <td style={{ ...td, textAlign: 'left' }}>{r.event}</td>
                <td style={{ ...td, color: r.importance === 3 ? RED : r.importance === 2 ? ORG : GRN }}>
                  {/* String.repeat throws a RangeError on a negative count, so a
                      malformed importance would crash the tab. Clamp to 0..3. */}
                  {'★'.repeat(Math.max(0, Math.min(3, Math.floor(Number(r.importance)) || 0)))}
                </td>
                <td style={td}>{r.forecast ?? '—'}</td>
                <td style={td}>{r.prior ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const th: React.CSSProperties = { padding: '3px 6px', textAlign: 'right', borderBottom: '1px solid #222', letterSpacing: 0.5 };
const td: React.CSSProperties = { padding: '3px 6px', textAlign: 'right', borderBottom: '1px solid #161616', color: '#e0e0e0' };

function Cent({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100%', color: '#333', fontSize: 11, letterSpacing: 2, fontFamily: FONT,
    }}>{children}</div>
  );
}
